import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DotsThree } from "@phosphor-icons/react";
import { BellSimpleIcon } from "@libs/zapi-ui/assets";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { useNotifications } from "../model/useNotifications";
import { AggregatedNotificationItem } from "./AggregatedNotificationItem";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { TNotification } from "../model/store";

type TNotificationTriggerProps = {
  projectId?: number;
  className?: string;
};

type TAggregatedNotification = {
  projectId: number;
  projectName: string;
  notifications: TNotification[];
  mostRecentDate: Date;
};

export function NotificationTrigger({ projectId, className }: TNotificationTriggerProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  const { notifications, isLoading, error, markAsRead, markAllAsRead, markAllProjectAsRead } = useNotifications({
    projectId
  });

  const aggregatedNotifications = useMemo(() => {
    const filteredNotifications = showUnreadOnly ? notifications.filter((n) => !n.isRead) : notifications;

    const grouped = filteredNotifications.reduce((acc, notification) => {
      const projId = notification.projectChapter?.projectId ?? notification.projectId ?? notification.project?.id;
      if (!projId) return acc;

      const projName =
        notification.projectChapter?.project.name ?? notification.project?.name ?? t("notifications.unknownProject");

      if (!acc[projId]) {
        acc[projId] = {
          projectId: projId,
          projectName: projName,
          notifications: [],
          mostRecentDate: new Date(notification.createdAt)
        };
      }

      acc[projId].notifications.push(notification);

      const notificationDate = new Date(notification.createdAt);
      if (notificationDate > acc[projId].mostRecentDate) {
        acc[projId].mostRecentDate = notificationDate;
      }

      return acc;
    }, {} as Record<number, TAggregatedNotification>);

    return Object.values(grouped).sort((a, b) => b.mostRecentDate.getTime() - a.mostRecentDate.getTime());
  }, [notifications, showUnreadOnly, t]);

  const handleToggleExpand = (projectId: number) => {
    setExpandedProjects((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const handleMarkAsRead = (id: number) => {
    markAsRead(id);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleMarkAllProjectAsRead = async (projectId: number) => {
    await markAllProjectAsRead(projectId);
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <AppButton
          appearance="text"
          size="icon"
          className={cn("relative flex-shrink-0", className)}
          aria-label={t("notifications.title")}
        >
          <BellSimpleIcon className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-innoveraRed-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-innoveraRed-400"></span>
            </span>
          )}
        </AppButton>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] overflow-hidden p-0 shadow-lg" sideOffset={8}>
        <div className="flex flex-col max-h-[600px]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-innoveraZinc-200">
            <FigmaTypography variant="heading-3" className="font-semibold text-innoveraZinc-900">
              {t("notifications.title")}
            </FigmaTypography>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <FigmaTypography variant="body-2" className="text-innoveraZinc-600 text-[14px]">
                  {t("notifications.onlyShowUnread")}
                </FigmaTypography>
                <Switch
                  checked={showUnreadOnly}
                  onCheckedChange={setShowUnreadOnly}
                  className="data-[state=checked]:bg-innoveraOrange-400"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <AppButton appearance="text" size="icon">
                    <DotsThree size={20} weight="bold" />
                  </AppButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleMarkAllAsRead} disabled={unreadCount === 0}>
                    {t("notifications.markAllAsRead")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-12 text-center">
                <FigmaTypography variant="body-2" className="text-innoveraZinc-500">
                  {t("notifications.loading")}
                </FigmaTypography>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <FigmaTypography variant="body-2" className="text-innoveraRed-400">
                  {error}
                </FigmaTypography>
              </div>
            ) : aggregatedNotifications.length === 0 ? (
              <div className="p-12 text-center">
                <BellSimpleIcon className="w-10 h-10 mx-auto mb-3 text-innoveraZinc-300" />
                <FigmaTypography variant="body-2" className="text-innoveraZinc-500">
                  {showUnreadOnly ? t("notifications.emptyUnread") : t("notifications.empty")}
                </FigmaTypography>
              </div>
            ) : (
              <div className="divide-y divide-innoveraZinc-100">
                {aggregatedNotifications.map((aggNotification) => (
                  <AggregatedNotificationItem
                    key={aggNotification.projectId}
                    notifications={aggNotification.notifications}
                    projectId={aggNotification.projectId}
                    projectName={aggNotification.projectName}
                    isExpanded={expandedProjects.has(aggNotification.projectId)}
                    onToggleExpand={() => handleToggleExpand(aggNotification.projectId)}
                    onMarkAsRead={handleMarkAsRead}
                    onMarkAllProjectAsRead={handleMarkAllProjectAsRead}
                    onClose={handleClose}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
